// projectPack.js - smaller saved projects (step 5).
//
// A saved project used to carry a full copy of every character / prop / animated-text animation
// (Lottie JSON, often 1-3 MB each) in every layer that uses it. The same character on 10 slides
// was stored 10 times.
//
// Saved format 2 ("packed"):
//   { "toonsFormat": 2, "assets": { "a1": <animation>, ... }, "slides": [ ...layers with
//     "JSONValue": { "$asset": "a1" } ... ] }
// Each distinct animation is stored once. Before comparing, the "_render" flags that lottie-web
// writes into the data while drawing are removed (they are rebuilt on every load), so copies of
// the same animation become identical.
//
// Old projects (a plain array of slides) are read exactly as before. Projects switch to format 2
// the next time they are saved. unpackProjectData() returns the same structure the editor has
// always used, so nothing else in the editor needs to know about the format.

export const PACKED_FORMAT = 2;

// Keys lottie-web adds to the animation data at runtime and rebuilds on every load.
const RUNTIME_KEYS = new Set(["_render"]);
const dropRuntimeKeys = (key, value) => (RUNTIME_KEYS.has(key) ? undefined : value);

// Animation as JSON text without the runtime keys. lottie-web only ever stores true/false in
// "_render", so the fast path removes those from the text (a quote inside a JSON string value is
// always escaped, so these patterns cannot match text content). Anything unexpected falls back
// to the slower, exact replacer.
function animationText(animation) {
  const text = JSON.stringify(animation)
    .replace(/,"_render":(?:true|false)/g, "")
    .replace(/"_render":(?:true|false),?/g, "");
  return text.indexOf('"_render":') === -1 ? text : JSON.stringify(animation, dropRuntimeKeys);
}

// Quick fingerprint of the text (length + about 8,000 sampled characters) used to find
// candidate duplicates. Equal fingerprints are always confirmed by comparing the full text, so
// two different animations can never be merged.
function fingerprint(text) {
  const step = Math.max(1, Math.floor(text.length / 8192));
  let h1 = 0x811c9dc5;
  let h2 = 5381;
  for (let i = 0; i < text.length; i += step) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = (Math.imul(h2, 33) + c) | 0;
  }
  return (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36) + text.length.toString(36);
}

function isAnimation(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value) && !("$asset" in value) && !("$sameAs" in value);
}

/**
 * Export payload (SlideData) with each distinct animation sent once. A layer whose animation is
 * identical to an earlier layer's gets "JSONValue": { "$sameAs": <index of that layer> }; the render
 * worker (src/sharedAnimations.js) puts the full animation back before rendering. The same character
 * on 10 slides was sent 10 times before (about 1.6 MB each). `layers` is not modified.
 */
export function packExportLayers(layers) {
  if (!Array.isArray(layers)) return JSON.stringify(layers);
  const byHash = new Map(); // hash -> [{ text, index }]
  let shared = 0;
  const out = layers.map((layer, index) => {
    if (!layer || !isAnimation(layer.JSONValue)) return layer;
    const text = animationText(layer.JSONValue);
    const hash = fingerprint(text);
    const candidates = byHash.get(hash) || [];
    const same = candidates.find((c) => c.text === text);
    if (same) {
      shared++;
      return { ...layer, JSONValue: { $sameAs: same.index } };
    }
    candidates.push({ text, index });
    byHash.set(hash, candidates);
    return layer;
  });
  return shared ? JSON.stringify(out) : JSON.stringify(layers);
}

function copy(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

/**
 * Returns the project data string to save. `slides` is the editor's slide array; it is not
 * modified. `omitKeys` are dropped everywhere (e.g. thumbnail-only fields).
 */
export function packProjectData(slides, omitKeys = []) {
  const omit = new Set(omitKeys);
  const replacer = (key, value) => (omit.has(key) ? undefined : value);

  if (!Array.isArray(slides)) return JSON.stringify(slides, replacer);

  const assetTexts = []; // [id, json text]
  const byHash = new Map(); // hash -> [{ id, text }]
  let next = 1;

  const assetIdFor = (animation) => {
    const text = animationText(animation);
    const hash = fingerprint(text);
    const candidates = byHash.get(hash) || [];
    const same = candidates.find((c) => c.text === text);
    if (same) return same.id;
    const id = "a" + next++;
    candidates.push({ id, text });
    byHash.set(hash, candidates);
    assetTexts.push([id, text]);
    return id;
  };

  const packedSlides = slides.map((slide) => {
    if (!slide || !Array.isArray(slide.LayerData)) return slide;
    return {
      ...slide,
      LayerData: slide.LayerData.map((layer) => {
        if (!layer || !isAnimation(layer.JSONValue)) return layer;
        return { ...layer, JSONValue: { $asset: assetIdFor(layer.JSONValue) } };
      }),
    };
  });

  if (next === 1) {
    // No animations in this project: keep the old, simpler format.
    return JSON.stringify(slides, replacer);
  }
  // Assembled as text so the (large) animations are not parsed and serialized a second time.
  // omitKeys are not applied inside animations (they are editor fields, not Lottie keys).
  const assetsText = assetTexts.map(([id, text]) => JSON.stringify(id) + ":" + text).join(",");
  return (
    '{"toonsFormat":' + PACKED_FORMAT +
    ',"assets":{' + assetsText + "}" +
    ',"slides":' + JSON.stringify(packedSlides, replacer) + "}"
  );
}

/** True for a parsed project in the packed format. */
export function isPackedProject(parsed) {
  return (
    parsed !== null &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    parsed.toonsFormat === PACKED_FORMAT &&
    Array.isArray(parsed.slides)
  );
}

/**
 * Takes the parsed project data (old or packed) and returns the slide array the editor uses,
 * with every layer's JSONValue restored (each layer gets its own copy, because the editor edits
 * them per layer). Old projects are returned unchanged.
 */
export function unpackProjectData(parsed) {
  if (!isPackedProject(parsed)) return parsed;
  const assets = parsed.assets || {};
  const used = new Set(); // the first layer takes the parsed object itself, later ones get copies
  return parsed.slides.map((slide) => {
    if (!slide || !Array.isArray(slide.LayerData)) return slide;
    slide.LayerData = slide.LayerData.map((layer) => {
      const ref = layer && layer.JSONValue && typeof layer.JSONValue === "object" ? layer.JSONValue.$asset : undefined;
      if (ref === undefined) return layer;
      if (!(ref in assets)) {
        // Should never happen; the character is then loaded from its file like any layer
        // without animation data.
        console.log("Project animation missing:", ref);
        return { ...layer, JSONValue: null };
      }
      if (!used.has(ref)) {
        used.add(ref);
        return { ...layer, JSONValue: assets[ref] };
      }
      return { ...layer, JSONValue: copy(assets[ref]) };
    });
    return slide;
  });
}

function randomId(length) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (let i = 0; i < length; i++) id += chars.charAt(Math.floor(Math.random() * chars.length));
  return id;
}

/**
 * Repairs projects saved by older versions of "Duplicate slide": slides that share a key (selecting
 * one selected both) and layers that share an id across slides (the preview mixed them up). The
 * first slide / layer keeps its key / id; later duplicates get new ones. Returns the number of
 * changes. Projects without such duplicates are not touched.
 */
export function makeSlideIdsUnique(slides) {
  if (!Array.isArray(slides) || !slides.some((s) => s && Array.isArray(s.LayerData))) return 0;
  let changed = 0;
  const keys = new Set();
  let maxKey = slides.reduce((m, s) => (s && typeof s.key === "number" && s.key > m ? s.key : m), -1);
  const ids = new Set();
  for (const slide of slides) {
    if (!slide || typeof slide !== "object") continue;
    if (keys.has(slide.key)) {
      slide.key = ++maxKey;
      changed++;
    }
    keys.add(slide.key);
    if (!Array.isArray(slide.LayerData)) continue;
    for (const layer of slide.LayerData) {
      if (!layer || typeof layer !== "object" || layer.AnimationId === undefined || layer.AnimationId === null) continue;
      if (ids.has(layer.AnimationId)) {
        let id;
        do id = randomId(8); while (ids.has(id));
        layer.AnimationId = id;
        changed++;
      }
      ids.add(layer.AnimationId);
    }
  }
  return changed;
}

/** JSON.parse + unpack, for the saved project string. */
export function parseProjectData(text) {
  const slides = unpackProjectData(JSON.parse(text));
  const fixed = makeSlideIdsUnique(slides);
  if (fixed) console.log(`Project: gave ${fixed} duplicated slide key(s) / layer id(s) a new value.`);
  // A thumbnail that was being made when the project was saved must not keep spinning after opening.
  if (Array.isArray(slides)) slides.forEach((s) => s && typeof s === "object" && s.slideLoader && (s.slideLoader = false));
  return slides;
}
